import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';

/**
 * The detailed view every metric card opens.
 *
 * A card that shows a number and does nothing when clicked is a dead end;
 * the ask was for every card, on every page, to open something. Most cards
 * have no page of their own, so this is the one they all get: the metric in
 * full - label, value, change, what it counts - plus whatever the page knows
 * about it (a breakdown, rows, a link to the module that owns the number).
 * Pages that know more pass `detail` on the card; the rest still open.
 *
 * detail: {
 *   description?: string,
 *   breakdown?: [{ label, value }],
 *   rows?: [{...}], columns?: [{ key, label }],
 *   to?: string, toLabel?: string,   // a route to the full page
 *   footnote?: string,
 * }
 */
const MetricDetailContext = createContext(null);

// `navigate` is injected (App passes react-router's) so the provider has no
// router dependency of its own; the CRA jest resolver cannot load the router's
// export map, and the view does not need one to render.
export const MetricDetailProvider = ({ children, navigate = (to) => { window.location.assign(to); }, pathname = null }) => {
  const [detail, setDetail] = useState(null);
  const open = useCallback((next) => setDetail({ ...next, openedAt: new Date(), from: window.location.pathname }), []);
  const close = useCallback(() => setDetail(null), []);
  const value = useMemo(() => ({ open, close }), [open, close]);

  // This provider sits above the router, so the sheet outlived the page it was
  // opened from: browser Back with a metric open left the *new* page sitting
  // under the overlay, and Radix keeps `pointer-events: none` on <body> while a
  // modal is open - so the whole app stopped responding to the mouse, with
  // nothing on screen to say why. A route change closes it.
  useEffect(() => { setDetail(null); }, [pathname]);
  const goFull = () => {
    if (!detail?.to) return;
    close();
    navigate(detail.to);
  };
  const breakdown = detail?.breakdown || [];
  const rows = detail?.rows || [];
  const columns = detail?.columns || (rows[0] ? Object.keys(rows[0]).map((key) => ({ key, label: key })) : []);
  return (
    <MetricDetailContext.Provider value={value}>
      {children}
      <Sheet open={!!detail} onOpenChange={(isOpen) => { if (!isOpen) close(); }}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
          {detail ? (
            <div className="space-y-5">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {detail.icon ? <detail.icon className="h-5 w-5 text-primary" /> : null}
                  {detail.label}
                </SheetTitle>
                <SheetDescription>
                  {detail.description || `Everything recorded behind "${detail.label}" on this page.`}
                </SheetDescription>
              </SheetHeader>

              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Value</p>
                <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">{detail.prefix}{detail.value}{detail.suffix}</p>
                {detail.hint ? <p className="mt-1 text-sm text-muted-foreground">{detail.hint}</p> : null}
                {typeof detail.deltaPct === 'number' && Number.isFinite(detail.deltaPct) ? (
                  <p className="mt-2 text-sm">
                    <Badge variant={detail.deltaPct > 0 ? 'success' : detail.deltaPct < 0 ? 'danger' : 'outline'}>
                      {detail.deltaPct > 0 ? '+' : ''}{detail.deltaPct}%
                    </Badge>
                    <span className="ml-2 text-muted-foreground">{detail.deltaLabel || 'vs last period'}</span>
                  </p>
                ) : null}
              </div>

              {breakdown.length ? (
                <div>
                  <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">Breakdown</p>
                  <table className="w-full text-sm">
                    <tbody>
                      {breakdown.map((row, index) => (
                        <tr key={`${row.label}-${index}`} className="border-b border-border/60 last:border-0">
                          <th scope="row" className="py-1.5 pr-3 text-left font-medium text-foreground">{row.label}</th>
                          <td className="py-1.5 text-right tabular-nums">{String(row.value ?? '-')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {rows.length ? (
                <div className="overflow-x-auto">
                  <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">Rows ({rows.length})</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                        {columns.map((column) => <th key={column.key} className="py-1.5 pr-3 font-medium">{column.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 200).map((row, index) => (
                        <tr key={row.id ?? index} className="border-b border-border/60 last:border-0">
                          {columns.map((column) => <td key={column.key} className="py-1.5 pr-3 tabular-nums">{String(row[column.key] ?? '-')}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              <div className="space-y-1 text-xs text-muted-foreground">
                <p>Page: {detail.from}</p>
                <p>Opened: {detail.openedAt.toLocaleString()}</p>
                {detail.footnote ? <p>{detail.footnote}</p> : null}
              </div>

              <div className="flex flex-wrap gap-2">
                {detail.to ? (
                  <Button onClick={goFull}><ArrowUpRight className="mr-2 h-4 w-4" />{detail.toLabel || 'Open full page'}</Button>
                ) : null}
                <Button variant="outline" onClick={close}>Close</Button>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </MetricDetailContext.Provider>
  );
};

// A no-op outside the provider so a card rendered in a test or a stray tree never throws.
export const useMetricDetail = () => useContext(MetricDetailContext) || { open: () => {}, close: () => {} };
